import { rem } from 'polished';
import React, { useCallback, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

import { Event as BigPictureEvent, BigPictureClient } from '@vimeo/bigpicture-client';
import { Progress } from '@vimeo/bokeh';
import { bokehTheme as theme } from '@vimeo/bokeh-theme';
import { User } from '@vimeo/gctl-api/models/components/schemas/user/User';
import { translate } from '@vimeo/i18n-runtime';
import { Button } from '@vimeo/iris/components';
import { Quota } from '@vimeo/iris/illustration';
import { Text, Header as H } from '@vimeo/iris/typography';
import { UpgradeBadge } from '@vimeo/monetization-upsells';
import { buildUpgradePlanUrl } from '@vimeo/picox';

import { useQuotaWarningFreeUsers } from '../../../hooks/use-quota-warning-free-users/useQuotaWarningFreeUsers';
import { bytesToSize } from '../../utils/bytesToSize';
import { matchPathToRoute } from '../../utils/matchPathToRoute';

export function percent(numerator: number, denominator: number) {
  return Math.round((numerator / denominator) * 100);
}

export function isNumber(n: unknown): n is number {
  return typeof n === 'number' && !isNaN(n);
}

interface Props {
  accountType?: User['membership']['type'];
  capabilities: {
    hasLivePremium?: boolean;
    hasEnterprise?: boolean;
    hasMonthlyBilling?: boolean;
    hasTotalStorageCap?: boolean;
  };
  uploadQuota: User['uploadQuota'];
  userInitiated: boolean;
  showing: boolean;
}

type QuotaUnit = 'video_count' | 'video_size';

function getUpsellMessage({
  hasVideoCountQuota,
  isOutOfQuota,
}: {
  hasVideoCountQuota: boolean;
  isOutOfQuota: boolean;
}) {
  switch (true) {
    case hasVideoCountQuota && !isOutOfQuota:
      return translate("You've almost reached your video limit. Upgrade for more!");
    case hasVideoCountQuota && isOutOfQuota:
      return translate("You've reached your video limit. Upgrade for more!");
    case !hasVideoCountQuota && !isOutOfQuota:
      return translate('You are almost out of space! Upgrade for more.');
    case !hasVideoCountQuota && isOutOfQuota:
    default:
      return translate('You are out of space! Upgrade for more.');
  }
}

function QuotaUpsell({
  percentUsed,
  linkText,
  upgradeHref,
  onUpgradeClick,
  quotaUnit,
}: {
  percentUsed: number;
  linkText: string;
  upgradeHref: string;
  onUpgradeClick: () => void;
  quotaUnit: QuotaUnit;
}) {
  const hasVideoCountQuota = quotaUnit === 'video_count';
  const isOutOfQuota = percentUsed >= 100;

  return (
    <UpsellContainer>
      <UpsellIllustration name={translate('Hands raised in celebration')} />
      <Header size="4">{getUpsellMessage({ hasVideoCountQuota, isOutOfQuota })}</Header>
      <UpgradeButton onClick={onUpgradeClick} variant="minimal" element="a" href={upgradeHref} pill>
        {linkText}
      </UpgradeButton>
    </UpsellContainer>
  );
}

const getBigPictureEventLocation = (showUpsell: boolean, ratioUsed: number) => {
  if (ratioUsed >= 1) {
    return 'at_limit_quota_widget';
  } else if (showUpsell) {
    return 'approaching_limit_quota_widget';
  } else {
    return 'quota_widget';
  }
};

const getQuotaPeriodLabel = (
  quotaPeriod: 'week' | 'month' | 'year' | 'lifetime',
  hasMonthlyBilling?: boolean,
  hasTotalStorageCap?: boolean,
): string => {
  switch (quotaPeriod) {
    case 'lifetime':
      return translate('Total');
    case 'year':
      return hasMonthlyBilling ? translate('Total') : translate('Annual');
    case 'month':
      return hasTotalStorageCap ? translate('Monthly') : translate('Total');
    case 'week':
    default:
      return translate('Weekly');
  }
};

const getPageName = (pathName: string) => {
  if (/^\/upload\/videos$/.test(pathName)) return 'upload_page';
  else return 'svv_manager';
};

const getQuotaAmount = (amount: number | null, quotaUnit: QuotaUnit): string => {
  return (quotaUnit === 'video_size' ? bytesToSize(amount) : amount).toString();
};

export function QuotaMenu({
  accountType,
  capabilities: { hasLivePremium, hasEnterprise, hasMonthlyBilling, hasTotalStorageCap },
  uploadQuota,
  userInitiated,
  showing,
}: Props) {
  /**
   * TODO: ACT-299: Show quota warning widget to free users who've reached 50% of their quota
   * https://vimean.atlassian.net/browse/ACT-299
   */
  const getQuotaWarningFreeUsersBucket = useQuotaWarningFreeUsers();

  const { lifetime, periodic, space } = uploadQuota;
  const useRestrictedQuota = uploadQuota.restricted != null;
  const effectiveUsed = useRestrictedQuota ? uploadQuota.restricted.used : space.used;
  const effectiveMax = useRestrictedQuota ? uploadQuota.restricted.max : space.max;
  const quotaPeriod = periodic.period ?? 'week';
  const quotaUnit = useRestrictedQuota
    ? uploadQuota.restricted.unit ?? 'video_size'
    : space.unit ?? 'video_size';

  const ratioUsed = effectiveMax ? (effectiveUsed ?? 0) / effectiveMax : 0;
  const percentUsed = Math.round(ratioUsed * 100);

  const quotaWarningFreeUsersBucket = accountType
    ? getQuotaWarningFreeUsersBucket(accountType, effectiveUsed ?? 0)
    : null;
  const showUpsell = quotaWarningFreeUsersBucket === 'variant' || ratioUsed > 2 / 3;
  const resolvedPaywallFeature =
    quotaUnit === 'video_size' && percentUsed >= 95 ? 'storage_limit' : 'quota';

  const hasVideoCountQuota = quotaUnit === 'video_count';
  const basicBigPictureEventData = useMemo(
    () => ({
      product: 'Upload',
      location: getBigPictureEventLocation(showUpsell, ratioUsed),
      upsell: useRestrictedQuota
        ? ('total_quota' as const)
        : space.showing === 'lifetime'
        ? ('total_quota' as const)
        : ('weekly_quota' as const),
      weekly_quota_percent_used: useRestrictedQuota
        ? null
        : isNumber(periodic.used) && isNumber(periodic.max)
        ? percent(periodic.used, periodic.max)
        : null,
      total_quota_percent_used: useRestrictedQuota
        ? isNumber(effectiveUsed) && isNumber(effectiveMax)
          ? percent(effectiveUsed, effectiveMax)
          : null
        : isNumber(lifetime.used) && isNumber(lifetime.max)
        ? percent(lifetime.used, lifetime.max)
        : null,
    }),
    [
      effectiveMax,
      effectiveUsed,
      lifetime.max,
      lifetime.used,
      periodic.max,
      periodic.used,
      ratioUsed,
      showUpsell,
      space.showing,
      useRestrictedQuota,
    ],
  );
  const linkProps = useMemo(
    () => ({
      text: showUpsell
        ? translate('Upgrade')
        : hasVideoCountQuota
        ? translate('Upgrade for more videos')
        : translate('Upgrade Storage'),
      href: buildUpgradePlanUrl(
        {
          paywallTrigger: 'quota_menu_upgrade_button',
          paywallLocation: 'quota_menu',
          paywallFeature: resolvedPaywallFeature,
        },
        {
          paywall: '1',
        },
      ),
    }),
    [hasVideoCountQuota, resolvedPaywallFeature, showUpsell],
  );

  useEffect(() => {
    if (showing) {
      const eventData = {
        file_size: null,
        user_initiated: userInitiated,
        path: window.location.pathname,
        page: matchPathToRoute(window.location.pathname),
        ...basicBigPictureEventData,
      };

      const event = new BigPictureEvent('vimeo.view_quota_upsell', 2, eventData);
      BigPictureClient.sendEvent(event);
    }
  }, [basicBigPictureEventData, showing, userInitiated]);

  const onUpgradeClick = useCallback(() => {
    const eventData = {
      copy: linkProps.text,
      target: linkProps.href,
      path: window.location.pathname,
      page: matchPathToRoute(window.location.pathname) ?? '',
      ...basicBigPictureEventData,
    };

    const event = new BigPictureEvent('vimeo.click_quota_upsell', 1, eventData);
    BigPictureClient.sendEvent(event);
  }, [basicBigPictureEventData, linkProps]);

  const trackUpgradeAction = useCallback(() => {
    const event = new BigPictureEvent('vimeo.upgrade_action', 30, {
      action_type: 'click',
      copy: linkProps.text,
      currency: null,
      discount_offer: null,
      is_discount: null,
      is_new_pricing: null,
      loading_time: null,
      location: 'storage_widget',
      page: getPageName(window.location.pathname),
      path: window.location.pathname,
      plan_selected: null,
      price: null,
      purchase_type: null,
      target: linkProps.href,
      target_path: linkProps.href,
      upsell_name: 'quota_meter',
      duration: null,
      feature: 'quota',
      upgrade_flags: null,
      promo_code_id: null,
      device_type: null,
    });
    BigPictureClient.sendEvent(event);
  }, [linkProps]);

  return (
    <Container>
      {showUpsell && (
        <QuotaUpsell
          percentUsed={percentUsed}
          linkText={linkProps.text}
          upgradeHref={linkProps.href}
          onUpgradeClick={onUpgradeClick}
          quotaUnit={quotaUnit}
        />
      )}
      <MainRow>
        <Text style={{ fontSize: `${rem(20)}` }}>
          {hasVideoCountQuota ? translate('Video usage') : translate('Storage')}
        </Text>
        <Text style={{ fontSize: `${rem(20)}` }}>{`${percentUsed}%`}</Text>
      </MainRow>
      <Row>
        <Progress
          value={percentUsed > 100 ? 100 : percentUsed}
          size="md"
          colorScheme="vimeoBlue"
          width="100%"
        />
      </Row>
      <Column>
        {useRestrictedQuota ? (
          <Row>
            {translate('Total')}
            <Usage>
              <span>{`${getQuotaAmount(effectiveUsed ?? 0, quotaUnit)} of ${getQuotaAmount(
                effectiveMax ?? 0,
                quotaUnit,
              )}`}</span>
            </Usage>
          </Row>
        ) : (
          <>
            {!!periodic.max && (
              <Row>
                {getQuotaPeriodLabel(quotaPeriod, hasMonthlyBilling, hasTotalStorageCap)}
                <Usage>
                  <span>{`${getQuotaAmount(periodic.used, quotaUnit)} of ${getQuotaAmount(
                    periodic.max,
                    quotaUnit,
                  )}`}</span>
                </Usage>
              </Row>
            )}
            {!!lifetime.max && (
              <Row hasTotalStorageCap={hasTotalStorageCap}>
                {translate('Total')}
                <Usage>
                  <span>{`${getQuotaAmount(lifetime.used, quotaUnit)} of ${getQuotaAmount(
                    lifetime.max,
                    quotaUnit,
                  )}`}</span>
                </Usage>
              </Row>
            )}
          </>
        )}
      </Column>
      {!(hasEnterprise || hasLivePremium) && !showUpsell && (
        <>
          <HR />
          <CTARow>
            <UpgradeBadge
              style={{
                borderRadius: rem(8),
                height: rem(40),
                maxWidth: rem(304),
                width: '100%',
                fontSize: rem(14),
                fontWeight: 700,
                lineHeight: rem(23),
              }}
              noMargin
              onClick={trackUpgradeAction}
              href={linkProps.href}
            >
              {linkProps.text}
            </UpgradeBadge>
          </CTARow>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  font-size: ${rem(13)};
  font-weight: 500;
  width: ${rem(322)};
  color: ${({ theme }) => theme.formats.soft};
  background-color: ${({ theme }) => theme.item.bg};
  border: 1px solid
    ${({ theme: t }) => (t.name === 'light' ? theme.colors.gray['100'] : theme.colors.gray['600'])};
  border-radius: ${rem(8)};
`;

const Row = styled.div<{ hasTotalStorageCap?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: ${rem(4)} ${rem(20)};
  color: ${({ hasTotalStorageCap, theme }) =>
    hasTotalStorageCap ? theme.elements.color5 : theme.formats.soft};
  font-weight: ${({ hasTotalStorageCap }) => hasTotalStorageCap && '400'};
`;

const MainRow = styled(Row)`
  margin: ${rem(14)} ${rem(0)};
`;

const CTARow = styled(Row)`
  margin: 0;
  padding: ${rem(8)};
`;

const HR = styled.hr`
  border: none;
  border-top: ${rem(1)} solid
    ${({ theme: t }) => (t.name === 'light' ? theme.colors.gray['100'] : theme.colors.gray['600'])};
  width: 100%;
`;

const Column = styled.div`
  margin-top: ${rem(10)};
  > :last-child {
    margin-bottom: ${rem(8)};
  }
`;

const Usage = styled.span`
  display: flex;
  align-items: center;
`;

const UpsellContainer = styled(Column)`
  background-color: ${({ theme }) => theme.elements.bg0};
  padding: ${rem(25)} ${rem(20)};
  margin: ${rem(6)};
  border-radius: ${rem(4)};
`;

const UPGRADE_GREEN = '#22B654';
const UPGRADE_BLUE = '#15C1CA';
export const UPGRADE_GRADIENT = `linear-gradient(-270deg, ${UPGRADE_GREEN}, ${UPGRADE_BLUE}, ${UPGRADE_GREEN}, ${UPGRADE_BLUE})`;

/* Animation keyframes for the upsell gradient */
export const gradient = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const UpgradeButton = styled(Button)`
  animation: ${gradient} 15s ease infinite;
  background: ${UPGRADE_GRADIENT};
  background-size: 300% 300%;
  color: ${theme.colors.white};
  margin-top: ${rem(20)};
  text-decoration: none;
  width: 100%;

  &:hover:not(:active),
  &:active {
    background: ${UPGRADE_GRADIENT};
    background-size: 300% 300%;
  }
`;

const UpsellIllustration = styled(Quota)`
  width: ${rem(120)};
  height: ${rem(52)};
  margin: 0 auto;
  display: block;
  margin-bottom: ${rem(25)};

  path {
    fill: ${({ theme }) => theme.content.color};
  }
`;

const Header = styled(H)`
  text-align: center;
`;
